/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosResponse,
} from "axios";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import { IApiError } from "@/interface/apiError.types";
import { COOKIE_CONFIG } from "@/config";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";

const baseURL = process.env.NEXT_PUBLIC_API_URL;

// Validate API URL
const validateApiUrl = () => {
  if (!baseURL) {
    throw new Error("NEXT_PUBLIC_API_URL is not configured");
  }

  try {
    const url = new URL(baseURL);
    if (!["http:", "https:"].includes(url.protocol)) {
      throw new Error("Invalid API URL protocol");
    }
    return baseURL;
  } catch {
    throw new Error("Invalid NEXT_PUBLIC_API_URL format");
  }
};

const axiosInstance = axios.create({
  baseURL: validateApiUrl(),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const refreshClient = axios.create({
  baseURL: validateApiUrl(),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const refreshToken = async () => {
  try {
    const response = await refreshClient.get(`/Identity/Refresh`);

    const newAccessToken = response?.data?.data?.accessToken;

    if (!newAccessToken) {
      throw new Error("Access token missing in refresh response");
    }

    const useProfileStore = (await import("@/store/useProfileStore")).default;
    useProfileStore.getState().setAccessToken(newAccessToken);

    setCookie(COOKIE_CONFIG.loggedIn, "true", {
      path: "/",
      maxAge: 30 * 24 * 60 * 60,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
    });
  } catch (error) {
    console.error("Refresh token failed", error);
    throw error;
  }
};


const logoutFlow = async (error: IApiError) => {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  // Logout user on 401 Unauthorized response
  deleteCookie(COOKIE_CONFIG.loggedIn);
  deleteCookie(COOKIE_CONFIG.profile);
  deleteCookie(COOKIE_CONFIG.version);
  deleteCookie(COOKIE_CONFIG.authorization, { path: "/" });
  const useProfileStore = (await import("@/store/useProfileStore")).default;
  useProfileStore.getState().clearProfile();
  if (window.location.pathname !== "/login") {
    showToast(
      TOAST_TYPES.error,
      error?.response?.data?.message ||
        "Your session has expired. Please log in again."
    );
    window.location.replace("/login");
  }
};

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    const useProfileStore = (await import("@/store/useProfileStore")).default;
    const accessToken =
      useProfileStore.getState().profileData?.accessToken ||
      getCookie(COOKIE_CONFIG.authorization)?.toString();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(
      error instanceof Error ? error : new Error(String(error))
    );
  }
);


let isRefreshing = false;
let isLoggingOut = false;
const requestQueue: Array<{
  config: InternalAxiosRequestConfig;
  resolve: (value: Promise<AxiosResponse>) => void;
  reject: (reason?: unknown) => void;
}> = [];

// Helper functions to reduce cognitive complexity
const createStandardError = (error: unknown): Error => {
  return error instanceof Error ? error : new Error(String(error));
};

const getSessionErrorCode = (error: AxiosError | IApiError): number | undefined => {
  const responseData = error?.response?.data as
    | {
        Errors?: Array<{ Code?: number }>;
        errors?: Array<{ code?: number }>;
      }
    | undefined;

  return responseData?.Errors?.[0]?.Code ?? responseData?.errors?.[0]?.code;
};

const isSessionExpiredError = (error: AxiosError | IApiError) => {
  const status = error?.response?.status;
  const errorCode = getSessionErrorCode(error);

  return status === 401 || (status === 403 && errorCode === 4031);
};

const validateResponseStructure = (response: AxiosResponse) => {
  if (!response?.data || typeof response.data !== "object") {
    const error = new Error(
      "Invalid response format: Expected an object response"
    );
    Object.assign(error, {
      response: {
        status: 400,
        data: {
          message: "Invalid response format: Expected an object response",
          error: "Bad Response Format",
        },
      },
    });
    throw error;
  }
  return response;
};

const queueRequest = (config: InternalAxiosRequestConfig) => {
  return new Promise<AxiosResponse>((resolve, reject) => {
    requestQueue.push({
      config,
      resolve: (requestPromise) => void requestPromise.then(resolve).catch(reject),
      reject,
    });
  });
};

const processQueuedRequests = (error?: Error) => {
  requestQueue.forEach(({ config, resolve, reject }) => {
    if (error) {
      reject(error);
      return;
    }

    resolve(axiosInstance(config));
  });
  requestQueue.length = 0;
};

const handleRetryAfterRefresh = async (
  originalRequest: InternalAxiosRequestConfig
) => {
  try {
    const response = await axiosInstance(originalRequest);
    processQueuedRequests();
    return response;
  } catch (error: unknown) {
    const errorResponse = error as IApiError;
    if (isSessionExpiredError(errorResponse)) {
      await logoutFlow(errorResponse);
    }
    return Promise.reject(createStandardError(error));
  }
};

const handleRefreshTokenFlow = async (error: AxiosError) => {
  try {
    await refreshToken();
    const originalRequest = error.response?.config;

    if (!originalRequest) {
      throw new Error("Original request config not found");
    }

    return handleRetryAfterRefresh(originalRequest);
  } catch (refreshError: unknown) {
    processQueuedRequests(createStandardError(refreshError));
    const errorResponse = refreshError as IApiError;
    await logoutFlow(errorResponse);
    return Promise.reject(createStandardError(refreshError));
  } finally {
    isRefreshing = false;
  }
};

const handleUnauthorizedError = async (error: AxiosError) => {
  // Only proceed with logout flow if we're not already on the login page
  if (window.location.pathname === "/login") {
    return Promise.reject(createStandardError(error));
  }

  // If a refresh is already in progress, queue the request
  if (isRefreshing) {
    if (!error.config) {
      return Promise.reject(createStandardError(error));
    }
    return queueRequest(error.config);
  }

  // Set the flag to indicate a token refresh is in progress
  isRefreshing = true;
  return handleRefreshTokenFlow(error);
};

// Response interceptor
axiosInstance.interceptors.response.use(
  async (response) => {
    // You can modify the response data here
    // Validate response structure
    return validateResponseStructure(response);
  },
  async (error) => {
    // Handle response errors here
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (isSessionExpiredError(error)) {
        return handleUnauthorizedError(error);
      }

      console.error("Response error:", error.response.data);
    } else if (error.request) {
      // The request was made but no response was received
      console.error("Request error:", error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error("Error:", error.message);
    }
    return Promise.reject(createStandardError(error));
  }
);

export default axiosInstance;

